import { NetSuiteCustomer, PaymentTerm } from "@/models/netsuite/netsuite";
import NetSuiteService from "@/services/netsuite_service";

/**
 * FUPOF-191: the checkout ("Quote Details") page can't render until the full
 * NetSuite customer record is in hand, and that SuiteQL read is slow enough to
 * put a blocking "Obtaining Customer Details" overlay in front of the user
 * every time they walk from the Cart to checkout.
 *
 * The customer is known as soon as it's assigned to the order, so the request
 * is started right then and parked here. By the time the user reaches checkout
 * it has usually resolved and the overlay is skipped entirely; if it hasn't,
 * checkout awaits the same in-flight promise behind the overlay — never a
 * second request.
 *
 * Payment terms ride along: a static lookup, fetched once per session.
 */

export interface CustomerDetails {
  customer: NetSuiteCustomer;
  paymentTerms: PaymentTerm[];
}

interface PrefetchEntry {
  customerNetSuiteId: string;
  request: Promise<CustomerDetails>;
  /** true once the request resolved with a usable customer record */
  resolved: boolean;
}

const netSuiteService = new NetSuiteService();

let entry: PrefetchEntry | null = null;
let paymentTermsRequest: Promise<PaymentTerm[]> | null = null;

/**
 * getNetSuiteCustomerById swallows its errors and hands back an empty object,
 * so a failed prefetch is only detectable by looking at the payload. Test for
 * emptiness rather than for a particular field: a guess at the response shape
 * that turns out wrong would silently discard every prefetch.
 */
function isUsable(details: CustomerDetails | null): boolean {
  return (
    !!details &&
    !!details.customer &&
    Object.keys(details.customer).length > 0
  );
}

/** Payment terms don't change within a session — one fetch serves every visit. */
export function loadPaymentTerms(): Promise<PaymentTerm[]> {
  if (!paymentTermsRequest) {
    const request = netSuiteService
      .getPaymentTerms()
      .then(terms => {
        // An empty list means the call failed (the service logs and returns
        // []). Drop the cache so the next caller retries.
        if (!terms.length && paymentTermsRequest === request) {
          paymentTermsRequest = null;
        }
        return terms;
      })
      .catch(err => {
        if (paymentTermsRequest === request) paymentTermsRequest = null;
        throw err;
      });
    paymentTermsRequest = request;
  }
  return paymentTermsRequest;
}

function fetchCustomerDetails(
  customerNetSuiteId: string
): Promise<CustomerDetails> {
  return Promise.all([
    netSuiteService.getNetSuiteCustomerById(customerNetSuiteId),
    loadPaymentTerms()
  ]).then(([customer, paymentTerms]) => ({ customer, paymentTerms }));
}

/**
 * The checkout page's Review round-trip snapshot.
 *
 * Walking Quote Details → Review → back destroys and recreates the checkout
 * component, and it has to come back holding the same customer data and NOT
 * re-run its form reset. That state used to live in module scope inside the
 * component, which put it out of reach of anything that needed to invalidate
 * it: a submit refreshes the customer, but a snapshot taken before the submit
 * would still be sitting there, and it wins over a fetch. Keeping it beside the
 * prefetch means one place decides when the cached customer is stale.
 *
 * Keyed by `order.customer_id` (a NetSuite id as a string, or "" for a general
 * customer) rather than the NetSuite id, so it can also tell a Review
 * round-trip apart from arriving at checkout for a different customer.
 */
export interface CheckoutSnapshot {
  customerId: string;
  customer: NetSuiteCustomer | null;
  paymentTerms: PaymentTerm[];
}

let checkoutSnapshot: CheckoutSnapshot | null = null;

export function saveCheckoutSnapshot(snapshot: CheckoutSnapshot): void {
  checkoutSnapshot = snapshot;
}

/** Read and discard the snapshot — exactly one initialize may consume it. */
export function takeCheckoutSnapshot(): CheckoutSnapshot | null {
  const snapshot = checkoutSnapshot;
  checkoutSnapshot = null;
  return snapshot;
}

export function clearCheckoutSnapshot(): void {
  checkoutSnapshot = null;
}

export function clearCustomerDetailsPrefetch(): void {
  entry = null;
  clearCheckoutSnapshot();
}

/**
 * Fire the request and publish it as the cached entry *immediately*, while it
 * is still in flight — that's what makes a concurrent caller (App's page-load
 * warm-up racing checkout's own hydration) join the same request instead of
 * issuing a second one. A request that fails, or comes back empty, drops the
 * entry so the next caller retries.
 */
function startRequest(id: string): PrefetchEntry {
  const started: PrefetchEntry = {
    customerNetSuiteId: id,
    resolved: false,
    request: fetchCustomerDetails(id)
  };
  entry = started;
  started.request.then(
    details => {
      if (isUsable(details)) {
        started.resolved = true;
      } else if (entry === started) {
        entry = null;
      }
    },
    () => {
      if (entry === started) entry = null;
    }
  );
  return started;
}

/**
 * Start (or keep) a background fetch of the customer's details. Safe to call
 * repeatedly: a prefetch already running or finished for the same customer is
 * left alone. Passing no id clears whatever is cached.
 */
export function prefetchCustomerDetails(
  customerNetSuiteId?: number | string | null
): void {
  if (!customerNetSuiteId) {
    clearCustomerDetailsPrefetch();
    return;
  }
  const id = customerNetSuiteId.toString();
  if (entry && entry.customerNetSuiteId === id) return;
  startRequest(id);
}

/**
 * Re-read this customer from NetSuite, discarding whatever is cached for them.
 *
 * Submitting a quote can change the customer record the checkout page renders
 * from — a quote for a person who wasn't in NetSuite yet makes the RESTlet
 * create a Contact under that customer — and everything cached here predates
 * that write: the entry below is held for the whole session, and the checkout
 * snapshot would let the next visit skip the fetch entirely. Both go, and the
 * fresh read starts now so the rep's next quote for this customer already has
 * the new contact in its Select Contact list.
 *
 * The API's own ten-minute customer cache is dropped by the submit endpoint, so
 * this read is genuinely fresh rather than the same stale payload again.
 */
export function refreshCustomerDetails(
  customerNetSuiteId?: number | string | null
): void {
  clearCustomerDetailsPrefetch();
  if (!customerNetSuiteId) return;
  startRequest(customerNetSuiteId.toString());
}

/**
 * True when this customer's details are already in hand, i.e. the caller can
 * skip the loading overlay because the await below will return immediately.
 */
export function hasCustomerDetails(
  customerNetSuiteId?: number | string | null
): boolean {
  if (!customerNetSuiteId || !entry) return false;
  return (
    entry.customerNetSuiteId === customerNetSuiteId.toString() && entry.resolved
  );
}

/**
 * The customer's details: the prefetched result when one exists (resolved or
 * still in flight), otherwise a fresh request. A prefetch that came back empty
 * is discarded and re-requested so a transient NetSuite failure doesn't leave
 * checkout with a blank customer.
 */
export async function loadCustomerDetails(
  customerNetSuiteId: number | string
): Promise<CustomerDetails> {
  const id = customerNetSuiteId.toString();
  const prefetched = entry && entry.customerNetSuiteId === id ? entry : null;
  if (prefetched) {
    let details: CustomerDetails | null = null;
    try {
      details = await prefetched.request;
    } catch (err) {
      details = null;
    }
    if (isUsable(details)) return details as CustomerDetails;
    if (entry === prefetched) entry = null;
  }
  // Nothing usable cached — start one and register it, so this result is
  // available to the next visit to checkout rather than being fetched again.
  return startRequest(id).request;
}
